/**
 * Split a full converted-deck HTML document into one self-contained fragment per slide — the one
 * coupling the render side shares with the import pipeline, kept here so every html-slide concern
 * has a single home.
 *
 * Each fragment carries the deck's shared stylesheet scaffold plus one slide's <section>, enough to
 * render that slide alone inside a Shadow DOM. The cache (see ./cache) uses it to normalise a
 * hosted (R2) slide document into a shadow-ready fragment.
 */

const CANVAS_W = 1280
const CANVAS_H = 720

// The shadow render pins each slide's stage to the deck's native 1280×720 and force-reveals any
// pre-paint FOUC-hidden object — a converted deck's own CSS assumes the loader's full-page context, so
// without this every animated object would stay invisible in the scriptless shadow render. Keep the
// <style> comment-free — DOMPurify drops a whole <style> whose CSS carries comments.
const SHADOW_OVERRIDE = `
<style data-aha-html-fit>
  :where(.aha-stage){width:${CANVAS_W}px!important;height:${CANVAS_H}px!important;min-height:0!important;
    display:block!important;overflow:hidden!important;position:relative!important;padding:0!important}
  :where(.aha-slide){transform:none!important;position:absolute!important;inset:0!important;margin:0!important}
  [data-fx-hide]{opacity:1!important}
</style>`

/**
 * Split a full converted-deck HTML document into one self-contained fragment per slide.
 * Returns [{ title, html }]. Each `html` = the deck's shared stylesheet <link>s + inline <style>s +
 * this slide's <section class="aha-stage"> + the shadow fit/reveal override — enough to render the
 * slide alone inside a Shadow DOM. Loader <script>s are dropped (the slide is static here).
 * The title is the one the BACKEND converter stamped on the <section> (`data-slide-title`) — the
 * server owns title derivation (worker/src/deck-slides.ts), so there is no client-side heuristic.
 * Browser-only (uses DOMParser).
 */
export function splitDeckHtml(deckHtml) {
  const doc = new DOMParser().parseFromString(deckHtml, 'text/html')
  const headBits = []
  doc.querySelectorAll('head link[rel="stylesheet"], head style').forEach((el) => {
    headBits.push(el.outerHTML)
  })
  const shared = headBits.join('\n')
  const sections = [...doc.querySelectorAll('body > section')]
  return sections.map((section) => ({
    title: section.getAttribute('data-slide-title') || '',
    html: `${shared}\n${section.outerHTML}\n${SHADOW_OVERRIDE}`,
  }))
}
